import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { ApolloError } from '@apollo/client';
import { GetShareCollectionAssets } from '@frameio/assets/shares/gql/generated/GetShareCollectionAssets';
import { SHARES_CURSOR_PAGE_SIZE } from '@frameio/assets/shares/hooks/useShareAssets';
import type {
  AddAssetsToShareInput,
  AddAssetsToShareMutationResult,
  Share,
} from '@frameio/graphql-client/generated';
import { useAddAssetsToShareMutation } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useOpenLinkInBrowserFromUxp } from '@frameio/shared/integrations/hooks/useOpenLinkInBrowserFromUxp';
import { getLink } from '@frameio/shared/routing';
import { useTrack } from '@frameio/shared/segment/hooks';
import { useShareActionsContext } from '@frameio/shared/shares/contexts/ShareActionsContext';
import { EMPTY_ARRAY } from '@frameio/utils';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import TEXT from './generated/TEXT/useAddAssetsToShare';
import { useRemoveAssetsFromShare } from './useRemoveAssetsFromShare';

TEXT.DEFINE({
  /** A message to display within a toast when an error occurs during the attempted update of a share link */
  error: 'There was a problem updating your Share Link',
  /** A label to display for the undo button */
  labelUndo: 'Undo',
  /** A label to display for the go to share button */
  labelGoToShare: 'Go to Share Link',
  /** A label to display for the create new share button */
  labelCreateNewShare: 'Create new Share Link',
  /** A label to display for the cancel button */
  labelCancel: 'Cancel',
  /** A message to display within a toast when the restricted assets limit is reached */
  restrictedAssetsLimitReached:
    'Can’t share items – this share has reached the 250 restricted item limit.',
  /** A message to display within a toast when assets are added to a share */
  added: ({ addedCount, name }: { addedCount: number; name: string }) =>
    plural(addedCount, {
      one: `1 new asset added to "${name}"`,
      other: `${addedCount} new assets added to "${name}"`,
    }),
  /** A message to display within a toast when no assets are added to a share */
  noneAdded: ({ count, name }: { count: number; name: string }) =>
    plural(count, {
      one: `Asset already exists in "${name}"`,
      other: `Assets already exist in "${name}"`,
    }),
});

const confirmationMessage = ({
  assetIdsFromMutationInput,
  addedAssetIds,
  share,
}: {
  assetIdsFromMutationInput: Array<string>;
  addedAssetIds: Array<string>;
  share?: Partial<Share> | null;
}): string => {
  if (!share) return '';

  const addedCount = addedAssetIds?.length;
  const name = share?.name;

  if (addedCount === 0 && name) {
    // nothing was added; check to see how many items they attempted to add for the appropriate message
    return TEXT.noneAdded({
      count: assetIdsFromMutationInput.length,
      name: name ?? '',
    })?.toString() as string;
  }

  return TEXT.added({ addedCount, name: name ?? '' })?.toString() as string;
};

type AddAssetsToShareParams = AddAssetsToShareInput & {
  projectId?: string;
};

export function useAddAssetsToShare() {
  const projectId = useActiveProjectId();
  const tryCatchOnAdd = useToastTryCatchHandler({ duration: null });
  const router = useRouter();
  const track = useTrack();
  const openLinkInBrowserFromUxp = useOpenLinkInBrowserFromUxp();
  const isWebClient = useIsMatchingClientName('web');

  const [addAssetsToShareMutation] = useAddAssetsToShareMutation();
  const removeAssetsFromShare = useRemoveAssetsFromShare();
  const { checkCreateShareRestrictedAssets } = useShareActionsContext();

  const addAssetsToShare = useCallback(
    async ({
      assetIds,
      shareId,
      projectId: inputProjectId,
    }: AddAssetsToShareParams): Promise<boolean> => {
      const resolvedProjectId = inputProjectId ?? projectId;
      // check assets list for restricted asset and present a confirmation
      // dialog to the user
      const { confirmed, hasRestrictedAssets } =
        await checkCreateShareRestrictedAssets(assetIds);

      if (!confirmed) {
        return false;
      }

      tryCatchOnAdd<AddAssetsToShareMutationResult>({
        attempt: () =>
          addAssetsToShareMutation({
            variables: {
              assetIds,
              shareId,
              shareRestrictedAssets: hasRestrictedAssets,
            },
            refetchQueries: [
              'GetShareAssets',
              {
                query: GetShareCollectionAssets,
                variables: {
                  shareId,
                  assetType: 'FOLDER',
                  page: {
                    first: SHARES_CURSOR_PAGE_SIZE,
                    afterOffset: 0,
                    mode: 'CURSOR',
                  },
                },
              },
              {
                query: GetShareCollectionAssets,
                variables: {
                  shareId,
                  assetType: 'FILE',
                  page: {
                    first: SHARES_CURSOR_PAGE_SIZE,
                    afterOffset: 0,
                    mode: 'CURSOR',
                  },
                },
              },
            ],
            update: (cache, { data }) => {
              const share = data?.addAssetsToShare?.share;

              cache.modify({
                id: cache.identify({
                  __typename: share?.__typename,
                  id: shareId,
                }),
                fields: (value, { fieldName }) => {
                  if (fieldName === 'aggregationInfo') {
                    return share?.aggregationInfo;
                  }

                  return value;
                },
              });
            },
          }),
        buttonCallback: (callbackResponse) => {
          removeAssetsFromShare({
            allowUndo: false,
            assetIds:
              callbackResponse?.data?.addAssetsToShare?.metadata
                ?.addedAssetIds ?? EMPTY_ARRAY,
            shareId,
          });

          track('share-assets-removed', {
            num_items_total:
              callbackResponse?.data?.addAssetsToShare?.metadata?.addedAssetIds
                .length,
            share_id: shareId,
          });
        },
        buttonLabel: TEXT.labelUndo,
        overwriteOptionsOnSuccess: (callbackResponse) =>
          callbackResponse.data?.addAssetsToShare.metadata.addedAssetIds
            .length === 0
            ? {
                buttonCallback: undefined,
              }
            : undefined,
        secondaryButtonCallback: () => {
          const href = getLink({
            page: 'shareBuilder',
            projectId: resolvedProjectId,
            shareId,
          });

          if (isWebClient) {
            router.push(href);
          } else {
            openLinkInBrowserFromUxp(href);
          }
        },
        secondaryButtonLabel: TEXT.labelGoToShare,
        successMessage({ data }: AddAssetsToShareMutationResult) {
          return confirmationMessage({
            assetIdsFromMutationInput: assetIds,
            addedAssetIds:
              data?.addAssetsToShare?.metadata?.addedAssetIds ?? [],
            share: data?.addAssetsToShare?.share,
          });
        },
        errorMessage(error: ApolloError) {
          if (
            error?.graphQLErrors[0]?.extensions?.code === 'FAILED_PRECONDITION'
          ) {
            return TEXT.restrictedAssetsLimitReached;
          }

          return TEXT.error;
        },
      });
      return true;
    },
    [
      checkCreateShareRestrictedAssets,
      tryCatchOnAdd,
      addAssetsToShareMutation,
      removeAssetsFromShare,
      track,
      projectId,
      isWebClient,
      router,
      openLinkInBrowserFromUxp,
    ]
  );

  return { addAssetsToShare };
}
